import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/cart";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, inr, formatApiError } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { items, remove, updateQty, clear, subtotal } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [poNumber, setPoNumber] = useState("");

  const tax = +(subtotal * 0.18).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const placeOrder = async () => {
    if (!user || !user.role) { nav("/login", { state: { from: "/cart" } }); return; }
    if (items.length === 0) return;
    setPlacing(true);
    try {
      const payload = {
        items: items.map((x) => ({
          product_id: x.product_id,
          variant_name: x.variant_name,
          quantity: x.quantity,
          notes: x.notes || "",
        })),
        po_number: poNumber.trim(),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed! Awaiting admin review and confirmation.");
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPlacing(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <h1 className="text-3xl font-heading font-semibold tracking-tight mb-8" data-testid="cart-title">Your cart</h1>
        {items.length === 0 ? (
          <div className="border border-gray-200 p-12 text-center">
            <p className="text-gray-500" data-testid="cart-empty">Your cart is empty.</p>
            <Button onClick={() => nav("/catalog")} className="mt-4 bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="cart-browse-btn">Browse catalog</Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-3">
              {items.map((it, i) => (
                <div key={`${it.product_id}-${it.variant_name}-${i}`} className="border border-gray-200 p-4 flex gap-4" data-testid={`cart-item-${i}`}>
                  <div className="w-20 h-20 bg-gray-50 flex-shrink-0">
                    {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover"/>}
                  </div>
                  <div className="flex-1">
                    <div className="font-heading font-medium">{it.product_name}</div>
                    <div className="text-xs text-gray-500">{it.variant_name}</div>
                    {it.notes && <div className="text-xs text-gray-500 mt-1 italic">"{it.notes}"</div>}
                    <div className="mt-2 flex items-center gap-3">
                      <Input type="number" min={1} value={it.quantity} onChange={(e) => updateQty(i, parseInt(e.target.value) || 1)} className="w-20 h-8 rounded-sm" data-testid={`cart-qty-${i}`}/>
                      <button onClick={() => remove(i)} className="text-gray-400 hover:text-red-600 text-sm inline-flex items-center gap-1" data-testid={`cart-remove-${i}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={1.5}/> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{inr(it.price * it.quantity)}</div>
                    <div className="text-xs text-gray-500">{inr(it.price)} each</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-gray-200 p-6 h-fit sticky top-20">
              <h2 className="font-heading font-semibold text-lg mb-4">Order summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span data-testid="summary-subtotal">{inr(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">GST (18%)</span><span data-testid="summary-tax">{inr(tax)}</span></div>
                <div className="border-t border-gray-200 my-2"/>
                <div className="flex justify-between font-medium text-base"><span>Total</span><span data-testid="summary-total">{inr(total)}</span></div>
              </div>
              <div className="mt-4">
                <label className="text-xs font-medium uppercase tracking-wider text-gray-600">PO number <span className="text-gray-400 normal-case">(optional)</span></label>
                <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Your reference PO #" className="rounded-sm mt-1" data-testid="cart-po-number"/>
              </div>
              <Button onClick={placeOrder} disabled={placing} className="w-full mt-6 bg-[#002fa7] hover:bg-[#00227b] rounded-sm h-12" data-testid="checkout-btn">
                {placing ? "Placing order…" : "Place order"}
              </Button>
              <p className="text-xs text-gray-500 mt-3">Your order will be reviewed by our team and confirmed as a sales order in Zoho Books.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
